import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import type { WithIsSiteAdminProps } from '../higher-order/index.ts';
import { withIsSiteAdmin } from '../higher-order/index.ts';

import { ChangeEditionSectionMessage } from './ChangeEditionSectionMessage.tsx';

const i18n = defineMessages({
	siteAdminContentRestrictionsTitle: {
		id: 'change-edition.section.message.site-admin.content-restrictions.title',
		defaultMessage: 'Upgrade for content-level restrictions',
		description: 'Upgrade for page restrictions title',
	},
	nonSiteAdminContentRestrictionsTitle: {
		id: 'change-edition.section.message.non-site-admin.content-restrictions.title',
		defaultMessage: 'Content-level restrictions are only available on paid plans',
		description: 'Page restrictions title for non-site admin',
	},
	spaceRestrictedPagesText: {
		id: 'change-edition.section.message.space-restricted-pages.text',
		defaultMessage:
			'You’re on the free plan, so you can only remove all restrictions from content by clicking the lock icon below. To set restrictions, upgrade your plan.',
		description: 'Warning message explaining why the form is not editable',
	},
	nonSiteAdminGrandfatheredRestrictedPagesBody: {
		id: 'change-edition.section.message.non-admin.grandfathered.space-restricted-pages.body',
		defaultMessage:
			'You’re on the free plan, so you can only remove all restrictions from content by clicking the lock icon below.',
		description: 'Warning message explaining why the form is not editable',
	},
	siteAdminRestrictedPagesBody: {
		id: 'change-edition.section.message.site-admin.space-restricted-pages.body',
		defaultMessage:
			'You’re on the free plan, so anyone who’s logged in can view and edit everything. To set restrictions, upgrade your plan.',
		description: 'Warning message explaining why the form is not editable for site admin',
	},
	nonSiteAdminRestrictedPagesBody: {
		id: 'change-edition.section.message.non-admin.space-restricted-pages.body',
		defaultMessage:
			'You’re on the free plan, so anyone who’s logged in can view and edit everything.',
		description: 'Warning message explaining why the form is not editable non-site admin',
	},
});

function getBody(isSiteAdmin, grandfathered) {
	if (grandfathered) {
		if (isSiteAdmin) {
			return i18n.spaceRestrictedPagesText;
		} else {
			return i18n.nonSiteAdminGrandfatheredRestrictedPagesBody;
		}
	} else {
		if (isSiteAdmin) {
			return i18n.siteAdminRestrictedPagesBody;
		} else {
			return i18n.nonSiteAdminRestrictedPagesBody;
		}
	}
}

type ChangeEditionSectionMessageSpaceContentComponentProps = {
	grandfathered: boolean;
};

type SiteAdminChangeEditionSectionMessageSpaceContentComponentProps =
	ChangeEditionSectionMessageSpaceContentComponentProps & WithIsSiteAdminProps;

const ChangeEditionSectionMessageSpaceContentComponent = ({
	grandfathered,
	isSiteAdmin,
}: SiteAdminChangeEditionSectionMessageSpaceContentComponentProps) => {
	const intl = useIntl();
	const bodyText = getBody(isSiteAdmin, grandfathered);
	const title = isSiteAdmin
		? i18n.siteAdminContentRestrictionsTitle
		: i18n.nonSiteAdminContentRestrictionsTitle;
	return (
		<ChangeEditionSectionMessage
			title={intl.formatMessage(title)}
			body={intl.formatMessage(bodyText)}
			source="individualSpaceRestrictedPages"
		/>
	);
};

ChangeEditionSectionMessageSpaceContentComponent.propTypes = {
	grandfathered: PropTypes.bool.isRequired,
	isSiteAdmin: PropTypes.bool.isRequired,
};

export const ChangeEditionSectionMessageSpaceContent =
	withIsSiteAdmin<ChangeEditionSectionMessageSpaceContentComponentProps>(
		ChangeEditionSectionMessageSpaceContentComponent,
	);
